import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Autocomplete from "@mui/material/Autocomplete";
import { InputBaseComponentProps } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { green, lightBlue } from "@mui/material/colors";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import CurrencyInput from "_shared/money/currencies/_views/currency-input";
import { Currency as CurrencyType } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import React, { forwardRef, useEffect, useRef } from "react";
import TextChange from "work/proposals/redlining/__diff/text-change";
import FeeRedline from "work/proposals/redlining/fee-schedule-redline/fee-redline/fee-redline";
import {
  editableRedlineDisplayProps,
  focusNextField,
  hasProposedChange,
  keepFocusOn,
  placeCaretAtEnd,
} from "work/proposals/redlining/field-redlines/_views/redline-field-focus";
import {
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";

const DiffContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "singleLine",
})<{ singleLine?: boolean }>(({ singleLine }) => ({
  width: "100%",
  ...(singleLine && {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
}));
const DiffResult = styled("span", {
  shouldForwardProp: (prop) => !["isDeferred", "isNewlyAdded", "change", "resolved", "acceptedResolved"].includes(prop.toString()),
})<{
  isDeferred?: boolean;
  isNewlyAdded?: boolean;
  change?: TextChange;
  resolved?: boolean;
  acceptedResolved?: boolean;
}>(({ isDeferred, isNewlyAdded, change, resolved, acceptedResolved, theme }) => ({
  backgroundColor: (function () {
    if (resolved) return "unset";
    if (change?.isAdded) return green[300];
  })(),
  color: (function () {
    if (change?.isRemoved && acceptedResolved) return theme.palette.text.disabled;
    if (isNewlyAdded) return lightBlue[700];
    if (isDeferred && !isNewlyAdded) return theme.palette.warning.main;
    return theme.palette.text.primary;
  })(),
  textDecoration: (function () {
    if (change?.isRemoved) return "line-through";
  })(),
  textDecorationColor: (function () {
    if (change?.isRemoved && acceptedResolved) return theme.palette.text.primary;
    if (change?.isRemoved && resolved) return lightBlue[700];
    if (change?.isRemoved) return theme.palette.error.main;
  })(),
  textDecorationThickness: '2px',
  '&:hover': {
    textDecorationColor: (function() { 
      let color = "initial";
      if (change?.isRemoved && resolved) color = lightBlue[700];
      if (change?.isRemoved && !resolved) color = theme.palette.error.main;
      return `oklch(from ${color} l c h / 0.25)`
    }())
  },
}));
const AcceptedRemovedFee = styled("span")(({ theme }) => ({
  color: theme.palette.text.disabled,
  textDecoration: "line-through",
  textDecorationColor: theme.palette.text.primary,
  textDecorationThickness: "2px",
}));
const TextContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "singleLine",
})<{ singleLine?: boolean }>(({ singleLine, theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  paddingRight: theme.spacing(2),
  width: "100%",
  // The flexible part of a table cell (see `redline-cell.tsx`): it takes the room beside the edit
  // control's slot, which keeps its own gap, and gives way rather than pushing the slot out.
  ...(singleLine && {
    flex: "1 1 auto",
    minWidth: 0,
    paddingRight: 0,
    width: "auto",
    "& > .MuiInputBase-input": {
      minWidth: 0,
    },
  }),
}));
const InputContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  "&.MuiInputBase-input": {
    height: "unset",
  },
}));
const PerSymbol = styled("span")(({ theme }) => ({
  fontSize: "1.5em",
  margin: theme.spacing(0, 1),
}));
const FeeRate = styled("span")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
}));
const UnitAutocomplete = styled(Autocomplete)(({ theme }) => ({
  flex: "1 1 10rem",
  minWidth: "8rem",
  maxWidth: "16rem",
  "& .MuiInputBase-root": {
    width: "100%",
  },
  [workPaneNarrowerThan(theme.breakpoints.values.sm)]: {
    maxWidth: "none",
  },
}));
const FixedFeeLabel = styled(FormControlLabel)(() => ({
  marginLeft: 0,
}));
const FeeInput = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(1),
}));
const CurrencySelect = styled(CurrencyInput)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.background.paper,
  }
}));
const UNIT_OPTIONS = ["Hour", "Day", "Week", "Month", "Year", "Each"] as const;
/** The amount: the control the fee's input opens on, and the one it keeps the focus in. */
const AMOUNT_INPUT = "input:not([type='hidden']):not([tabindex='-1'])";
/**
 * Where the fee's own pop-ups draw: the currency's menu and the unit's list. They sit outside the fee's
 * inputs on the page, so the focus moving into one has not left the fee.
 */
const FEE_POPUP = ".MuiPopover-root, .MuiAutocomplete-popper";

export interface DiffFeeProps extends InputBaseComponentProps {
  className?: string;
  feeRedline: FeeRedline | null;
  isNewlyAdded: boolean;
  acceptedResolved?: boolean;
  acceptedResolvedRemoval?: boolean;
  acceptedRemovedLabel?: string;
  showTextInput?: boolean;
  /** Draws the fee on one line, cut off with an ellipsis where it does not fit — a table cell's shape. */
  singleLine?: boolean;
  /** The fee can be edited: its display takes the focus, and focusing it opens the inputs (#6624). */
  editable?: boolean;
  /** The fee's name, which its display announces when it takes the focus. */
  fieldLabel?: string;
  /** The fee the inputs hold, or undefined while they hold no amount: an empty amount is not zero (#6627). */
  onFeeInputChange?: (fee: Fee | undefined) => void;
  /** Opens the inputs, when the display takes the focus. */
  onDisplayFocus?: () => void;
  /** Commits the fee the inputs hold, and says whether it could: a fee with no amount cannot. */
  onTextInputSubmit?: () => boolean;
  onTextInputCancel?: () => void;
}

const DiffFee = forwardRef<HTMLInputElement, DiffFeeProps>(
  function DiffFee(props, ref) {
    const {
      className,
      feeRedline,
      isNewlyAdded,
      acceptedResolved,
      acceptedResolvedRemoval,
      acceptedRemovedLabel,
      showTextInput,
      singleLine,
      editable,
      fieldLabel,
      onFeeInputChange,
      onDisplayFocus,
      onTextInputSubmit,
      onTextInputCancel
    } = props;

    const feeInputRef = useRef<HTMLDivElement>(null);
    // Set by Escape, which closes the inputs itself: the blur that follows must not commit what it reverted.
    const revertingRef = useRef(false);

    // The inputs open on the amount, with the caret after it.
    useEffect(() => {
      if (!showTextInput) return;
      revertingRef.current = false;
      const amount = feeInputRef.current?.querySelector<HTMLInputElement>(AMOUNT_INPUT);
      if (!amount) return;
      amount.focus();
      placeCaretAtEnd(amount);
    }, [showTextInput]);

    const [currentFeeRate, setCurrentFeeRate] = React.useState<Money | undefined>(() => editableRate(feeRedline));
    const [currentFeeUnit, setCurrentFeeUnit] = React.useState<string>(() => editableUnit(feeRedline) ?? "");
    const [isFixedFee, setIsFixedFee] = React.useState<boolean>(() => editableUnit(feeRedline) === null);

    const handleFixedFeeChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setIsFixedFee((prevValue) => !prevValue);
      onFeeInputChange?.(feeFrom(currentFeeRate, event.target.checked ? null : currentFeeUnit));
    };

    // The unit is drawn only for a fee that is not fixed, so a unit change is never a fixed fee's.
    const handleFeeUnitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setCurrentFeeUnit(event.target.value);
      onFeeInputChange?.(feeFrom(currentFeeRate, event.target.value));
    };

    const handleFeeRateChange = (currency: CurrencyType, amount: number | undefined) => {
      // A cleared field leaves no rate, and so no fee: it used to go out as zero, and a blur then
      // committed $0.00 that nobody typed (#6627).
      const rate = amount === undefined ? undefined : Money.create({ amount, currency: currency ?? CurrencyType.USD });
      setCurrentFeeRate(rate);
      onFeeInputChange?.(feeFrom(rate, isFixedFee ? null : currentFeeUnit));
    };

    const getChangeKey = (change: TextChange): string => {
      let changeType: string;
      if (change.isAdded) {
        changeType = "added";
      } else if (change.isRemoved) {
        changeType = "removed";
      } else {
        changeType = "unchanged";
      }
      return `${change?.diff.value}_${changeType}`;
    };

    // Amount, currency, unit and Fixed are one fee: it commits when the focus leaves all of them, so
    // choosing a currency never commits half a fee. One that cannot be committed keeps the focus.
    const handleFeeInputBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget;
      if (next && event.currentTarget.contains(next)) return;
      if (next instanceof Element && next.closest(FEE_POPUP)) return;
      if (revertingRef.current) return;
      const amount = event.currentTarget.querySelector<HTMLInputElement>(AMOUNT_INPUT);
      if (onTextInputSubmit?.() === false && amount) keepFocusOn(amount);
    };

    const handleFeeInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      // A key the currency's menu or the unit's list already answered is theirs, not the fee's.
      if (event.defaultPrevented) return;
      if (event.key === KeyboardKey.Escape) {
        // The fee's own Escape, not the drawer's it sits in.
        event.stopPropagation();
        revertingRef.current = true;
        onTextInputCancel?.();
        (event.target as HTMLElement).blur();
        return;
      }
      if (event.key !== KeyboardKey.Enter) return;
      event.preventDefault();
      // Moving on is what commits: the fee's blur does it, or keeps the focus when it cannot.
      focusNextField(event.currentTarget);
    };

    // The inputs open on the fee as it stands, not on what they held when Escape last closed them.
    const handleDisplayFocus = () => {
      const unit = editableUnit(feeRedline);
      setCurrentFeeRate(editableRate(feeRedline));
      setCurrentFeeUnit(unit ?? "");
      setIsFixedFee(unit === null);
      onDisplayFocus?.();
    };

    return (
      <TextContainer singleLine={singleLine}>
        <input ref={ref} type="hidden" />
        <InputContainer className={className}>
          {!showTextInput && (
            <DiffContainer
              singleLine={singleLine}
              {...(editable && editableRedlineDisplayProps(fieldLabel, hasProposedChange(feeRedline?.textChanges)))}
              onFocus={editable ? handleDisplayFocus : undefined}
            >
              {acceptedResolvedRemoval && !feeRedline?.textChanges.length ? (
                <AcceptedRemovedFee>{acceptedRemovedLabel}</AcceptedRemovedFee>
              ) : feeRedline?.textChanges.map((change: TextChange) => {
                return (
                  <DiffResult
                    key={getChangeKey(change)}
                    isDeferred={feeRedline.currentEntry === null}
                    isNewlyAdded={isNewlyAdded}
                    change={change}
                    resolved={feeRedline.isResolved}
                    acceptedResolved={acceptedResolved}
                  >
                    {change?.diff.value}
                  </DiffResult>
                );
              })}
            </DiffContainer>
          )}
          {showTextInput && (
            <>
              {/* No "previously:" / "your changes:" preview above the input: the fee's own history
                  control carries its history, and repeating it beside the editor spent the room the
                  editor itself needed. */}
              <FeeInput ref={feeInputRef} onBlur={handleFeeInputBlur} onKeyDown={handleFeeInputKeyDown}>
                <FeeRate>
                  <CurrencySelect
                    allowNegative={false}
                    size="small"
                    label="Amount"
                    variant="standard"
                    value={currentFeeRate}
                    onChange={handleFeeRateChange}
                  />
                  {!isFixedFee && (
                    <>
                      <PerSymbol>/</PerSymbol>
                      <UnitAutocomplete
                        freeSolo
                        options={UNIT_OPTIONS}
                        inputValue={currentFeeUnit}
                        disabled={!currentFeeRate?.amount}
                        onInputChange={(_event, value) => {
                          handleFeeUnitChange({
                            target: { value },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit"
                            size="small"
                            variant="standard"
                          />
                        )}
                      />
                    </>
                  )}
                </FeeRate>
                <FixedFeeLabel
                  label="Fixed"
                  labelPlacement="end"
                  control={
                    <Checkbox
                      size="small"
                      edge="start"
                      color="primary"
                      icon={<CheckBoxOutlineBlankIcon />}
                      checked={isFixedFee}
                      onChange={handleFixedFeeChange}
                    />
                  }
                />
              </FeeInput>
            </>
          )}
        </InputContainer>
      </TextContainer>
    );
  }
);

/**
 * The fee the inputs open on: the one in effect, else the one asked for, else the original — the
 * entry `FeeRedlineInput` edits. One entry is chosen before its parts are read, because a fixed fee's
 * unit is null: reading each part on its own would pass over that null to an older fee's unit (#6624).
 */
function editableFee(feeRedline: FeeRedline | null): Fee | undefined {
  if (!feeRedline) return undefined;
  if (feeRedline.currentEntry !== undefined) return feeRedline.currentEntry || undefined;
  if (feeRedline.revisedEntry !== false) return feeRedline.revisedEntry || undefined;
  return feeRedline.originalEntry || undefined;
}

/** The rate the fee's inputs open on. */
function editableRate(feeRedline: FeeRedline | null): Money | undefined {
  return editableFee(feeRedline)?.rate;
}

/** The unit the fee's inputs open on: null is a fixed fee, undefined is no fee at all. */
function editableUnit(feeRedline: FeeRedline | null): string | null | undefined {
  const fee = editableFee(feeRedline);
  return fee ? fee.unit : undefined;
}

/** The fee a rate and a unit make, or none while there is no rate. */
function feeFrom(rate: Money | undefined, unit: string | null): Fee | undefined {
  return rate ? Fee.create({ rate, unit }) : undefined;
}

export default DiffFee;
